import { type Store } from '@reduxjs/toolkit';

import { type CreateMultipartResponse, type UploadUrlResponse } from '@air/api/types';
import {
  isLarge,
  queuedOrPreparingUploadsSelector,
  setLargeUploadUrlInfoAction,
  setUploadUrlInfoAction,
  type Upload,
  uploadByIdSelector,
} from '@air/redux-uploader';

import { type AirState } from '~/store/types';

type UploadInProgress<T> = {
  promise?: Promise<T | undefined>;
  resolve?: (data: T | undefined) => void;
  reject?: (error: Error) => void;
};

type SmallUploadInProgress = UploadInProgress<UploadUrlResponse['0']>;
type LargeUploadInProgress = UploadInProgress<CreateMultipartResponse['0']>;

const MAX_UPLOADS_TO_SEND = 10;

export type ShouldBatchTogetherParams<TMetadata> = {
  requestedUpload: Upload;
  otherUpload: Upload;
  requestedMetadata: TMetadata;
  otherMetadata: TMetadata;
};

export type OnUploadUrlsFetchedCallback<TMetadata> = (params: {
  metadata: TMetadata;
  response: UploadUrlResponse | CreateMultipartResponse;
}) => void;

/**
 * Abstract base class for managing batched upload URL fetching.
 * Handles promise management and batching logic, while delegating
 * metadata retrieval and API calls to subclasses.
 *
 * @template TMetadata - The type of metadata associated with uploads
 */
export abstract class BaseUploadUrlsManager<TMetadata> {
  protected store: Store<AirState>;
  #onUploadUrlsFetched?: OnUploadUrlsFetchedCallback<TMetadata>;
  #smallUploadFetchUrlInProgress: Map<string, SmallUploadInProgress> = new Map();
  #largeUploadFetchUrlInProgress: Map<string, LargeUploadInProgress> = new Map();

  constructor({
    store,
    onUploadUrlsFetched,
  }: { store: Store<AirState>; onUploadUrlsFetched?: OnUploadUrlsFetchedCallback<TMetadata> }) {
    this.store = store;
    this.#onUploadUrlsFetched = onUploadUrlsFetched;
  }

  /**
   * Get metadata for a specific upload.
   * @param uploadId The ID of the upload
   * @returns The metadata or undefined if not found
   */
  protected abstract getUploadMetadata(uploadId: string): TMetadata | undefined;

  /**
   * Determine if two uploads should be batched together.
   * @param requestedMetadata Metadata of the requested upload
   * @param otherMetadata Metadata of another upload to consider batching
   * @returns true if uploads should be batched together
   */
  protected abstract shouldBatchTogether(params: ShouldBatchTogetherParams<TMetadata>): boolean;

  /**
   * Make API call to fetch small upload URLs.
   * @param uploads Array of small uploads to process
   * @param metadata Metadata from the requested upload
   * @returns Promise resolving to array of upload URL responses
   */
  protected abstract fetchSmallUploadUrls(uploads: Upload[], metadata: TMetadata): Promise<UploadUrlResponse>;

  /**
   * Make API call to create large multipart uploads.
   * @param uploads Array of large uploads to process
   * @param metadata Metadata from the requested upload
   * @returns Promise resolving to array of multipart responses
   */
  protected abstract fetchLargeUploadUrls(uploads: Upload[], metadata: TMetadata): Promise<CreateMultipartResponse>;

  /**
   * Starts fetching upload URLs in batch.
   * Finds up to MAX_UPLOADS_TO_SEND queued uploads that should be batched together,
   * creates promises for them, and initiates API calls for both small and large uploads.
   * @param uploadId The ID of the upload to fetch URL for
   */
  protected fetchUploadUrls(uploadId: string): void {
    const requestedUpload = uploadByIdSelector(this.store.getState(), uploadId);
    if (!requestedUpload) {
      return;
    }
    const requestedUploadMetadata = this.getUploadMetadata(uploadId);
    if (!requestedUploadMetadata) {
      return;
    }

    const queuedUploads = queuedOrPreparingUploadsSelector(this.store.getState());

    const uploadsToProcess: Upload[] = [requestedUpload];

    for (const queuedUpload of queuedUploads) {
      if (queuedUpload.id === uploadId) {
        // current upload already exists in the array
        continue;
      }

      // Skip uploads that are already in progress
      if (
        this.#smallUploadFetchUrlInProgress.has(queuedUpload.id) ||
        this.#largeUploadFetchUrlInProgress.has(queuedUpload.id)
      ) {
        continue;
      }

      const uploadMetadata = this.getUploadMetadata(queuedUpload.id);
      if (
        uploadMetadata &&
        this.shouldBatchTogether({
          requestedUpload: requestedUpload,
          otherUpload: queuedUpload,
          requestedMetadata: requestedUploadMetadata,
          otherMetadata: uploadMetadata,
        })
      ) {
        uploadsToProcess.push(queuedUpload);
        if (uploadsToProcess.length >= MAX_UPLOADS_TO_SEND) {
          break;
        }
      }
    }

    const smallUploads: Upload[] = [];
    const largeUploads: Upload[] = [];

    uploadsToProcess.forEach((upload) => {
      if (isLarge(upload)) {
        largeUploads.push(upload);
        const promise = new Promise<CreateMultipartResponse['0'] | undefined>((resolve, reject) => {
          this.#largeUploadFetchUrlInProgress.set(upload.id, {
            ...this.#largeUploadFetchUrlInProgress.get(upload.id),
            resolve,
            reject,
          });
        });
        this.#largeUploadFetchUrlInProgress.set(upload.id, {
          ...this.#largeUploadFetchUrlInProgress.get(upload.id),
          promise,
        });
      } else {
        smallUploads.push(upload);
        const promise = new Promise<UploadUrlResponse['0'] | undefined>((resolve, reject) => {
          this.#smallUploadFetchUrlInProgress.set(upload.id, {
            ...this.#smallUploadFetchUrlInProgress.get(upload.id),
            resolve,
            reject,
          });
        });

        this.#smallUploadFetchUrlInProgress.set(upload.id, {
          ...this.#smallUploadFetchUrlInProgress.get(upload.id),
          promise,
        });
      }
    });

    if (smallUploads.length > 0) {
      this.fetchSmallUploadUrls(smallUploads, requestedUploadMetadata)
        .then((response) => {
          response.forEach((urlInfo, index) => {
            const uploadId = smallUploads[index]?.id;
            if (uploadId) {
              this.store.dispatch(setUploadUrlInfoAction({ uploadId, uploadUrlInfo: urlInfo }));
              this.#smallUploadFetchUrlInProgress.get(uploadId)?.resolve?.(urlInfo);
            }
          });
          this.#onUploadUrlsFetched?.({ metadata: requestedUploadMetadata, response });
        })
        .catch((error) => {
          smallUploads.forEach((upload) => {
            this.#smallUploadFetchUrlInProgress.get(upload.id)?.reject?.(error);
          });
        })
        .finally(() => {
          smallUploads.forEach((upload) => {
            this.#smallUploadFetchUrlInProgress.delete(upload.id);
          });
        });
    }

    if (largeUploads.length > 0) {
      this.fetchLargeUploadUrls(largeUploads, requestedUploadMetadata)
        .then((response) => {
          response.forEach((multipartInfo, index) => {
            const uploadId = largeUploads[index]?.id;
            if (uploadId) {
              this.store.dispatch(setLargeUploadUrlInfoAction({ uploadId, uploadUrlInfo: multipartInfo }));
              this.#largeUploadFetchUrlInProgress.get(uploadId)?.resolve?.(multipartInfo);
            }
          });
          this.#onUploadUrlsFetched?.({ metadata: requestedUploadMetadata, response });
        })
        .catch((error) => {
          largeUploads.forEach((upload) => {
            this.#largeUploadFetchUrlInProgress.get(upload.id)?.reject?.(error);
          });
        })
        .finally(() => {
          largeUploads.forEach((upload) => {
            this.#largeUploadFetchUrlInProgress.delete(upload.id);
          });
        });
    }
  }

  public getSmallUploadUrl = async (uploadId: string): Promise<UploadUrlResponse[0] | undefined> => {
    if (this.#smallUploadFetchUrlInProgress.has(uploadId)) {
      return this.#smallUploadFetchUrlInProgress.get(uploadId)?.promise;
    }

    this.fetchUploadUrls(uploadId);
    return this.#smallUploadFetchUrlInProgress.get(uploadId)?.promise;
  };

  public getLargeUploadUrl = async (uploadId: string): Promise<CreateMultipartResponse[0] | undefined> => {
    if (this.#largeUploadFetchUrlInProgress.has(uploadId)) {
      return this.#largeUploadFetchUrlInProgress.get(uploadId)?.promise;
    }

    this.fetchUploadUrls(uploadId);
    return this.#largeUploadFetchUrlInProgress.get(uploadId)?.promise;
  };
}
